import {padNum, texture} from "p3";
import definitions from "../definitions";
import common from "../common";
import { locale } from "../utils";

export default class SceneLoader extends PIXI.Container {
    constructor() {
        super();

        this._files = [];
        this._callback = null;
        this._loaded = 0;

        this._spinner = null;
        this._spinnerTween = null;

        this._loadProgressBinding = null;

        this.init();
    }

    init() {
        this.interactive = true;

        const bg = new PIXI.Sprite(texture("ui_loader"));
        bg.width = common.STAGE_WIDTH;
        this.addChild(bg);

        this._spinner = new PIXI.Container();
        this._spinner.position.set(common.STAGE_WIDTH * 0.5, 400);
        this.addChild(this._spinner);

        this._spinner.ground = new PIXI.Sprite(texture("loading_patch"));
        this._spinner.ground.position.set(0, 180);
        this._spinner.ground.anchor.set(0.5, 1);
        this._spinner.addChild(this._spinner.ground);

        let frames = [];
        for (let i = 0; i < 2; ++i) {
            frames[i] = texture("loading_" + padNum(i+1, 3));
        }
        let sequence = new P3.MovieClipSequence(frames);
        this._spinner.chars = new P3.MovieClip(sequence, "default", false);
        this._spinner.chars.position.set(0, 200);
        this._spinner.chars.anchor.set(0.5, 1);
        this._spinner.chars.animationSpeed = 3;
        this._spinner.chars.play(true);
        this._spinner.addChild(this._spinner.chars);

        frames = [];
        for (let i = 0; i < 2; ++i) {
            frames[i] = texture("loading_grass_" + padNum(i+1, 3));
        }
        sequence = new P3.MovieClipSequence(frames);
        this._spinner.grass = new P3.MovieClip(sequence, "default", false);
        this._spinner.grass.position.set(0, 200);
        this._spinner.grass.anchor.set(0.5, 1);
        this._spinner.grass.animationSpeed = 3;
        this._spinner.grass.play(true);
        this._spinner.addChild(this._spinner.grass);

        this._enteringText = new PIXI.BitmapText(locale('ui_loading_entering'), {
            fontName: "Alghera Pro",
            fontSize: 30,
            align: "center",
            tint: 0x26160e
        });
        this._enteringText.position.set(0, 170);
        this._enteringText.anchor.set(0.5);
        this._spinner.addChild(this._enteringText);

        this._levelName = new PIXI.BitmapText('', {
            fontName: "Alghera Pro",
            fontSize: 60,
            align: "center",
            tint: 0x26160e,
            maxWidth: 600
        });
        this._levelName.position.set(0, 200);
        this._levelName.anchor.set(0.5, 0);
        this._spinner.addChild(this._levelName);
    }

    destroy() {
        this.stopAnimation();

        this.unload(false);

        if (this._loadProgressBinding !== null) {
            this._loadProgressBinding.detach();
        }
        super.destroy({ children: true });
    }

    update() {
        this._spinner.chars.update(window.dt);
        this._spinner.grass.update(window.dt);
    }

    /**
     * @returns {Promise}
     */
    animateIn() {
        return new Promise((resolve) => {
            this.stopAnimation();

            this.alpha = 1;
            this.visible = true;

            resolve();
        });
    }

    /**
     * @returns {Promise}
     */
    animateOut() {
        return new Promise((resolve) => {
            this.stopAnimation();

            this._spinnerTween = gsap.timeline({ onComplete: resolve });
            this.parent.addTween(this._spinnerTween);

            this._spinnerTween.add(gsap.to(this, 0.5, {
                delay: 0.5,
                alpha: 0,
                ease: Power2.easeInOut,
                onComplete: () => this.visible = false
            }));
        });
    }

    stopAnimation() {
        if (this._spinnerTween) {
            this.parent.removeTween(this._spinnerTween, true);
            this._spinnerTween = null;
        }
    }

    /**
     * @param {String} title
     */
    setTitle(title) {
        if (title) {
            this._levelName.text = title;
            this._enteringText.visible = true;
        } else {
            this._levelName.text = '';
            this._enteringText.visible = false;
        }
    }

    /**
     * @param {array<object>} manifest
     * @param {function} callback
     */
    load(manifest, callback) {
        let files = [];
        for (let i = 0, count = manifest.length; i < count; ++i) {
            const file = manifest[i];
            file.metadata = Object.assign({}, { unload: definitions.SCENE_LOADER_ASSET.REQUIRED }, file.metadata);

            if (!this._files.find(x => file.name === x.name)) {
                files.push(file);
                this._files.push(file);
            }
        }

        this._callback = callback;

        if (files.length > 0) {
            const basePath = "./";

            const assets = P3.assets;
            assets.addFiles(files, basePath + "assets/");

            this._loadProgressBinding = assets.signals.progress.add(this._onLoadProgress, this);
            assets.signals.complete.once(this._onLoadComplete, this);
            assets.load();
        } else {
            this._onLoadComplete();
        }
    }

    /**
     * @param {boolean} [temporary=false]
     */
    unload(temporary) {
        for (let i = this._files.length - 1; i >= 0; --i) {
            const file = this._files[i];

            if (file.metadata.unload !== definitions.SCENE_LOADER_ASSET.PERSISTENT && (temporary && file.metadata.unload !== definitions.SCENE_LOADER_ASSET.TEMPORARY))
                continue;

            P3.assets.unload(file.name);

            this._files.splice(i, 1);
        }
    }

    /**
     * @param {PIXI.Loader} loader
     * @private
     */
    _onLoadProgress(loader) {
        console.log("loading " + loader.progress + "%");
    }

    /**
     * @private
     */
    _onLoadComplete() {
        this._loaded = 1;

        this._callback && this._callback();
        this.animateOut().then(() => this.parent && this.parent.removeChild(this));
    }

    /**
     * @returns {number}
     */
    get loaded() {
        return this._loaded;
    }
}